import { matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
	ROUTE_SPORTSBOOK_BROWSE,
	ROUTE_SPORTSBOOK_MY_BETS,
	ROUTE_SPORTSBOOK_LIVE_PAGE,
	ROUTE_SPORTSBOOK_SPORTS_PAGE,
	ROUTE_SPORTSBOOK_LEAGUES,
	ROUTE_SPORTSBOOK_EVENTS,
	ROUTE_SPORTSBOOK_EXPLORE_PAGE,
	ROUTE_SPORTSBOOK_MY_BETS_OPEN
} from '../../../../router/constants';
import useContextualPathTransformer from '../../../../../hooks/use-contextual-path-transformer';
import SportsbookTextKeys from '../../sportsbook-text-keys';
import SportsbookTopNavigationLink from './sportsbook-top-navigation-link';
import { Translation } from '../../../../translation/types';
import { trackSportsbookUseTopNavPills } from '@comeon/analytics';
import { memo, useMemo } from 'react';
import SportsbookHorizontalScrollArea from '../../components/sportsbook-horizontal-scroll-area';

const StyledHorizontalScrollAreaView = styled(SportsbookHorizontalScrollArea.View)`
	gap: 12px;
`;

const HorizontalScrollableArea = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
	return (
		<SportsbookHorizontalScrollArea.Root className={className}>
			<StyledHorizontalScrollAreaView>{children}</StyledHorizontalScrollAreaView>
		</SportsbookHorizontalScrollArea.Root>
	);
};

const BROWSE_ROUTES = [
	ROUTE_SPORTSBOOK_BROWSE,
	ROUTE_SPORTSBOOK_SPORTS_PAGE,
	ROUTE_SPORTSBOOK_LEAGUES,
	ROUTE_SPORTSBOOK_EVENTS
];

type SportsbookTopMenuProps = {
	className?: string;
	translations: Translation;
	openBetsCount: number;
	onLinkClick: () => void;
	hasMyBets: boolean;
};

// Using only matchPath for all routes, wrapped in memo. This will prevent unnecessary re-renders by each useMatch that was used before.
const useNavigationMatches = () => {
	const { pathname } = useLocation();

	return useMemo(
		() => ({
			isExploreMatch: !!matchPath(
				{
					path: ROUTE_SPORTSBOOK_EXPLORE_PAGE,
					end: true
				},
				pathname
			),
			isLiveMatch: !!matchPath(
				{
					path: ROUTE_SPORTSBOOK_LIVE_PAGE,
					end: false
				},
				pathname
			),
			isMyBetsMatch: !!matchPath(
				{
					path: ROUTE_SPORTSBOOK_MY_BETS,
					end: false
				},
				pathname
			),
			isBrowseMatch: !!BROWSE_ROUTES.find(route =>
				matchPath(
					{
						path: route,
						end: false
					},
					pathname
				)
			)
		}),
		[pathname]
	);
};

/**
 * Display sportsbook top navigation
 */
const SportsbookTopNavigation: React.FC<SportsbookTopMenuProps> = memo(
	({ className, translations, openBetsCount, onLinkClick, hasMyBets = true }) => {
		const { isExploreMatch, isLiveMatch, isMyBetsMatch, isBrowseMatch } = useNavigationMatches();
		const transformPath = useContextualPathTransformer();

		return (
			<HorizontalScrollableArea className={className}>
				<SportsbookTopNavigationLink
					isActive={isExploreMatch}
					onClick={() => {
						onLinkClick();
						trackSportsbookUseTopNavPills(ROUTE_SPORTSBOOK_EXPLORE_PAGE);
					}}
					label={translations[SportsbookTextKeys.EXPLORE_NAV_LABEL]}
					dataAt="sb-top-navigation-link-explore"
					to={transformPath(ROUTE_SPORTSBOOK_EXPLORE_PAGE, {
						preserveContexts: true
					})}
				/>
				<SportsbookTopNavigationLink
					isActive={isLiveMatch}
					onClick={() => {
						onLinkClick();
						trackSportsbookUseTopNavPills(ROUTE_SPORTSBOOK_LIVE_PAGE);
					}}
					label={translations[SportsbookTextKeys.LIVE_GAMES_PAGE_NAV_LABEL]}
					dataAt="sb-top-navigation-link-live"
					to={transformPath(ROUTE_SPORTSBOOK_LIVE_PAGE, {
						preserveContexts: true
					})}
				/>
				{hasMyBets && (
					<SportsbookTopNavigationLink
						isActive={isMyBetsMatch}
						onClick={() => {
							onLinkClick();
							trackSportsbookUseTopNavPills(ROUTE_SPORTSBOOK_MY_BETS);
						}}
						label={translations[SportsbookTextKeys.MY_BETS_NAV_LABEL]}
						badgeLabel={openBetsCount > 0 ? openBetsCount : undefined}
						dataAt="sb-top-navigation-link-my-bets"
						to={transformPath(ROUTE_SPORTSBOOK_MY_BETS_OPEN, {
							preserveContexts: true
						})}
					/>
				)}
				<SportsbookTopNavigationLink
					isActive={isBrowseMatch}
					onClick={() => {
						onLinkClick();
						trackSportsbookUseTopNavPills(ROUTE_SPORTSBOOK_BROWSE);
					}}
					label={translations[SportsbookTextKeys.BROWSE_NAV_LABEL]}
					dataAt="sb-top-navigation-link-browse"
					to={transformPath(ROUTE_SPORTSBOOK_BROWSE, {
						preserveContexts: true
					})}
				/>
			</HorizontalScrollableArea>
		);
	}
);

SportsbookTopNavigation.displayName = 'SportsbookTopNavigation';

export default SportsbookTopNavigation;
